/* Who can see what, and finding a time together.
 *
 * Two halves that belong on one screen because they are the same subject seen
 * from two sides: what other people may know about your time, and what you do
 * with the fact that you know something about theirs.
 *
 * The sharing half is written as a list of **sentences about categories**
 * rather than a permissions matrix. "Gym — anyone with my code can see the
 * title" is a thing somebody can check at a glance and disagree with; a grid of
 * ticks with people down one edge and categories along the other is a thing
 * nobody reads, and an unread permission screen is worse than none, because it
 * looks like it has been reviewed.
 *
 * Everything here is governed by lib/social's resolver: the person, then a
 * group they are in, then the category's default, then nothing. On top of
 * that sit the controls that only ever take away — pause, how far ahead, which
 * hours, one person shown nothing — and a preview that answers "what does
 * Dana actually see" with the same rules the real page uses.
 */

import Section from '../ui/Section';
import Icon from '../ui/Icon';
import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { get, post, put, del, errorMessage } from '../api';
import { useToast } from '../ui/toast';
import { useSession, localToday } from '../session';
import { hhmm, toMinuteOfDay, friendlyDate } from '../ui/time';
import Meetings from './people/Meetings';
import Friends from './people/Friends';
import { ConfirmDialog } from '../ui/schedule';

type Level = 'private' | 'busy' | 'titles';

interface ShareType { id: string; name: string; color: string; visibility: Level; isPublic: boolean }
interface Circle {
  id: string; name: string;
  members: { id: string; name: string; email: string }[];
  shares: { typeId: string; visibility: Level }[];
}
interface Person { id: string; name: string; email?: string; status?: string }

interface PrivacySettings {
  paused: boolean;
  horizonDays: number | null;
  sharePast: boolean;
  windowStart: number | null;
  windowEnd: number | null;
  unfiled: 'private' | 'busy';
  findTime: boolean;
}

interface Social {
  code: string;
  circles: Circle[];
  people: Person[] | { people?: Person[] };
  types: ShareType[];
  levels: Level[];
  settings?: PrivacySettings;
  horizons?: number[];
  hidden?: string[];
  personShares?: { viewerId: string; typeId: string; visibility: Level }[];
}

interface PreviewDay {
  date: string;
  blocks: { startMin: number; endMin: number; title: string; typeName: string | null }[];
  limited?: 'paused' | 'hidden' | 'nothing' | 'horizon' | 'past' | null;
}

/* The three answers, in the words somebody would use rather than the words the
   database uses. */
const LEVEL_COPY: Record<Level, { label: string; says: string }> = {
  private: { label: 'Nobody', says: 'Not shared. Anybody looking sees only that you are not free.' },
  busy: { label: 'That I am busy', says: 'They see the hours are taken, and nothing about what is in them.' },
  titles: { label: 'What it is', says: 'They see the name of the thing — "Gym", "Design review".' }
};

const DEFAULTS: PrivacySettings = {
  paused: false, horizonDays: null, sharePast: true,
  windowStart: null, windowEnd: null, unfiled: 'private', findTime: true
};

const HORIZON_LABEL = (d: number) =>
  d === 1 ? 'Tomorrow' : d === 7 ? 'A week' : d === 14 ? 'Two weeks' : d === 30 ? 'A month'
    : d === 90 ? 'Three months' : d === 365 ? 'A year' : `${d} days`;

const WHY_EMPTY: Record<string, string> = {
  paused: 'Sharing is paused, so they see nothing at all.',
  hidden: 'You show them nothing, so they see nothing at all.',
  nothing: 'Nothing of yours is shared with them.',
  horizon: 'That day is further ahead than you let people look.',
  past: 'Days that have gone are not shared.'
};

export default function PeopleScreen() {
  const qc = useQueryClient();
  const toast = useToast();
  const { today, user } = useSession();
  const [code, setCode] = useState('');
  const [circleName, setCircleName] = useState('');
  const [rotating, setRotating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewAs, setPreviewAs] = useState('connection');
  const [previewDate, setPreviewDate] = useState(today ?? localToday(user?.tz));

  const social = useQuery({
    queryKey: ['social'],
    queryFn: async () => {
      const r = await get<Social>('/api/social');
      if (!r.ok) throw new Error(errorMessage(r, 'Could not read your sharing.'));
      return r;
    }
  });

  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['social'] });
    void qc.invalidateQueries({ queryKey: ['social-preview'] });
  };

  const connect = useMutation({
    mutationFn: async () => {
      const r = await post<{ person: { name: string }; already: boolean }>(
        '/api/social/connect', { code: code.trim() });
      if (!r.ok) throw new Error(errorMessage(r, 'Could not use that code.'));
      return r;
    },
    onSuccess: r => {
      refresh();
      setCode('');
      setError(null);
      toast.say(r.already
        ? `You are already connected to ${r.person.name}.`
        : `Connected to ${r.person.name}. They see nothing yet — that is the next part.`, 'good');
    },
    onError: (e: Error) => setError(e.message)
  });

  const rotate = useMutation({
    mutationFn: async () => {
      const r = await post<{ code: string }>('/api/social/code/rotate', {});
      if (!r.ok) throw new Error(errorMessage(r, 'Could not change your code.'));
      return r.code;
    },
    onSuccess: () => {
      refresh();
      setRotating(false);
      toast.say('New code. The old one has stopped working for anybody who has not used it yet.');
    },
    onError: (e: Error) => toast.say(e.message, 'bad')
  });

  const setVisibility = useMutation({
    mutationFn: async ({ t, body }: { t: ShareType; body: Record<string, unknown> }) => {
      const r = await put(`/api/social/types/${t.id}/visibility`, body);
      if (!r.ok) throw new Error(errorMessage(r, 'Could not change that.'));
      return r;
    },
    onSuccess: refresh,
    onError: (e: Error) => toast.say(e.message, 'bad')
  });

  const saveSettings = useMutation({
    mutationFn: async (patch: Partial<PrivacySettings>) => {
      const r = await put('/api/social/settings', patch);
      if (!r.ok) throw new Error(errorMessage(r, 'Could not change that.'));
      return patch;
    },
    onSuccess: patch => {
      refresh();
      if (patch.paused === true) toast.say('Sharing is paused. Nobody sees anything until you turn it back on.');
      if (patch.paused === false) toast.say('Sharing is back on, exactly as you left it.', 'good');
    },
    onError: (e: Error) => toast.say(e.message, 'bad')
  });

  const hide = useMutation({
    mutationFn: async ({ p, hidden }: { p: Person; hidden: boolean }) => {
      const r = await put(`/api/social/people/${p.id}/hidden`, { hidden });
      if (!r.ok) throw new Error(errorMessage(r, 'Could not change that.'));
      return { p, hidden };
    },
    onSuccess: ({ p, hidden }) => {
      refresh();
      toast.say(hidden
        ? `${p.name} now sees nothing of your time. They are not told.`
        : `${p.name} sees what your settings share again.`);
    },
    onError: (e: Error) => toast.say(e.message, 'bad')
  });

  const personShare = useMutation({
    mutationFn: async ({ p, typeId, visibility }: { p: Person; typeId: string; visibility: Level | null }) => {
      const r = await put(`/api/people/${p.id}/shares`, { typeId, visibility });
      if (!r.ok) throw new Error(errorMessage(r, 'Could not change that.'));
      return r;
    },
    onSuccess: refresh,
    onError: (e: Error) => toast.say(e.message, 'bad')
  });

  const addCircle = useMutation({
    mutationFn: async () => {
      const r = await post('/api/social/circles', { name: circleName.trim() });
      if (!r.ok) throw new Error(errorMessage(r, 'Could not make that group.'));
      return circleName.trim();
    },
    onSuccess: n => { refresh(); setCircleName(''); toast.say(`${n} is a group now.`, 'good'); },
    onError: (e: Error) => toast.say(e.message, 'bad')
  });

  const circleShare = useMutation({
    mutationFn: async ({ circle, typeId, visibility }:
      { circle: Circle; typeId: string; visibility: Level }) => {
      const r = await put(`/api/social/circles/${circle.id}/shares`, { typeId, visibility });
      if (!r.ok) throw new Error(errorMessage(r, 'Could not change that.'));
      return r;
    },
    onSuccess: refresh,
    onError: (e: Error) => toast.say(e.message, 'bad')
  });

  const addMember = useMutation({
    mutationFn: async ({ circle, viewerId }: { circle: Circle; viewerId: string }) => {
      const r = await post(`/api/social/circles/${circle.id}/members`, { viewerId });
      if (!r.ok) throw new Error(errorMessage(r, 'Could not add them.'));
      return r;
    },
    onSuccess: refresh,
    onError: (e: Error) => toast.say(e.message, 'bad')
  });

  const dropMember = useMutation({
    mutationFn: async ({ circle, viewerId }: { circle: Circle; viewerId: string }) => {
      const r = await del(`/api/social/circles/${circle.id}/members/${viewerId}`);
      if (!r.ok) throw new Error(errorMessage(r, 'Could not take them out.'));
      return r;
    },
    onSuccess: refresh,
    onError: (e: Error) => toast.say(e.message, 'bad')
  });

  const dropCircle = useMutation({
    mutationFn: async (c: Circle) => {
      const r = await del(`/api/social/circles/${c.id}`);
      if (!r.ok) throw new Error(errorMessage(r, 'Could not remove that.'));
      return c;
    },
    onSuccess: c => { refresh(); toast.say(`${c.name} is no longer a group.`); },
    onError: (e: Error) => toast.say(e.message, 'bad')
  });

  const preview = useQuery({
    queryKey: ['social-preview', previewAs, previewDate],
    enabled: !!social.data && /^\d{4}-\d{2}-\d{2}$/.test(previewDate),
    queryFn: async () => {
      const r = await get<{ as: string; day: PreviewDay }>(
        `/api/social/preview?as=${encodeURIComponent(previewAs)}&date=${previewDate}`);
      if (!r.ok) throw new Error(errorMessage(r, 'Could not show that.'));
      return r;
    }
  });

  const d = social.data;
  const types = d?.types ?? [];
  const circles = d?.circles ?? [];
  const settings = d?.settings ?? DEFAULTS;
  const horizons = d?.horizons ?? [1, 3, 7, 14, 30, 90, 365];
  const hidden = new Set(d?.hidden ?? []);
  const personShares = d?.personShares ?? [];
  /* `people.list` answers with a shape the older screen expected; take either. */
  const connections: Person[] = Array.isArray(d?.people)
    ? d!.people as Person[]
    : ((d?.people as { people?: Person[] })?.people ?? []);
  const accepted = connections.filter(p => !p.status || p.status === 'accepted');

  const shared = types.filter(t => t.visibility !== 'private' || t.isPublic).length;

  /* The hours window is two fields that only mean something together, so it
     is edited locally and saved when both ends are real. */
  const [windowOn, setWindowOn] = useState(false);
  const [winStart, setWinStart] = useState('08:00');
  const [winEnd, setWinEnd] = useState('20:00');
  useEffect(() => {
    if (!d?.settings) return;
    setWindowOn(d.settings.windowStart !== null);
    if (d.settings.windowStart !== null) setWinStart(hhmm(d.settings.windowStart));
    if (d.settings.windowEnd !== null) setWinEnd(hhmm(Math.min(1439, d.settings.windowEnd)));
  }, [d?.settings?.windowStart, d?.settings?.windowEnd]);          // eslint-disable-line react-hooks/exhaustive-deps

  const saveWindow = (on: boolean, s = winStart, e = winEnd) => {
    if (!on) { saveSettings.mutate({ windowStart: null, windowEnd: null }); return; }
    if (!/^\d{2}:\d{2}$/.test(s) || !/^\d{2}:\d{2}$/.test(e)) return;
    const end = e === '23:59' ? 1440 : toMinuteOfDay(e);
    saveSettings.mutate({ windowStart: toMinuteOfDay(s), windowEnd: end });
  };

  const ownSetting = (p: Person, typeId: string) =>
    personShares.find(s => s.viewerId === p.id && s.typeId === typeId)?.visibility ?? '';

  return (
    <div className="screen">
      <header className="topbar">
        <div>
          <h1>People</h1>
          <p className="muted">
            {settings.paused
              ? 'Sharing is paused — nobody sees anything'
              : shared === 0
                ? 'Nothing about your time is shared with anybody'
                : `${shared} of ${types.length} categories are visible to somebody`}
          </p>
        </div>
      </header>

      {social.isError && (
        <div className="notice notice-bad" role="alert">
          <p><strong>Could not read your sharing.</strong></p>
          <p className="muted small">{(social.error as Error).message}</p>
        </div>
      )}

      {settings.paused && (
        <div className="notice notice-strong privacy-paused" role="status">
          <span className="notice-icon"><Icon name="lock" size={18} /></span>
          <div>
            <p><strong>Sharing is paused.</strong></p>
            <p className="muted small">Nobody — connections, groups or anyone with your code — sees anything,
              and friends cannot find times with you. Every setting below is kept for when you turn it back on.</p>
          </div>
          <button className="btn btn-primary btn-sm" onClick={() => saveSettings.mutate({ paused: false })}>
            <Icon name="eye" size={15} />Share again
          </button>
        </div>
      )}

      {/* ── the people you want to see ───────────────────────── */}
      {/* First, because it is the reason the rest of this screen exists: codes,
          sharing and groups are how Amon comes to know when somebody is free. */}
      <Friends connections={accepted} />

      {/* ── the code ─────────────────────────────────────────── */}
      <Section id="code-title"
               icon="link"
               title="Your code"
               meta="hand it over; it grants nothing by itself"
               info={<>Somebody who uses this becomes a connection. A connection sees exactly what you set below and nothing else — the code opens a door into an empty room until you decide otherwise.</>}>

        <div className="code-row">
          <code className="share-code">{d?.code ?? '··········'}</code>
          <button className="btn btn-ghost btn-sm"
                  onClick={() => {
                    if (d?.code) void navigator.clipboard?.writeText(d.code)
                      .then(() => toast.say('Copied.'))
                      .catch(() => toast.say('Your browser would not let me copy it.', 'bad'));
                  }}><Icon name="copy" size={16} />Copy</button>
          <button className="btn btn-ghost btn-sm" onClick={() => setRotating(true)}>
            <Icon name="refresh" size={16} />New code
          </button>
        </div>

        <form className="inline wrap" onSubmit={(e: FormEvent) => {
          e.preventDefault();
          if (!code.trim()) { setError('Paste a code first.'); return; }
          setError(null);
          connect.mutate();
        }}>
          <input value={code} maxLength={40} placeholder="Somebody else's code"
                 aria-label="Somebody else's code"
                 onChange={e => setCode(e.target.value)} />
          <button className="btn btn-primary btn-sm" type="submit" disabled={connect.isPending}>
            {connect.isPending ? 'Connecting…' : 'Connect'}
          </button>
        </form>
        {error && <p className="auth-err" role="alert">{error}</p>}

        {connections.length > 0 && (
          <p className="muted small">
            Connected to {connections.map(p => p.name).join(', ')}.
          </p>
        )}
      </Section>

      {/* ── how much, for everybody ──────────────────────────── */}
      <Section id="privacy-title"
               icon="lock"
               title="Privacy"
               meta="these only ever show less"
               info={<>Everything else on this screen decides <em>which</em> of your time somebody sees.
          These decide how much of it, for everybody at once — and none of them can show anything
          your other settings do not already share.</>}>

        <ul className="privacy-list">
          <li>
            <label className="check-field">
              <input type="checkbox" checked={settings.paused}
                     onChange={e => saveSettings.mutate({ paused: e.target.checked })} />
              <span><strong>Pause all sharing</strong>
                <em className="muted small">Nobody sees anything. Nothing you have set is lost.</em></span>
            </label>
          </li>

          <li className="privacy-row">
            <label htmlFor="privacy-horizon"><Icon name="calendar" size={15} />How far ahead people can look</label>
            <select id="privacy-horizon" value={settings.horizonDays ?? ''}
                    onChange={e => saveSettings.mutate({
                      horizonDays: e.target.value ? Number(e.target.value) : null
                    })}>
              <option value="">No limit</option>
              {horizons.map(h => <option key={h} value={h}>{HORIZON_LABEL(h)}</option>)}
            </select>
          </li>

          <li>
            <label className="check-field">
              <input type="checkbox" checked={settings.sharePast}
                     onChange={e => saveSettings.mutate({ sharePast: e.target.checked })} />
              <span><strong>Share days that have gone</strong>
                <em className="muted small">Off, people can only see today onwards.</em></span>
            </label>
          </li>

          <li>
            <label className="check-field">
              <input type="checkbox" checked={windowOn}
                     onChange={e => { setWindowOn(e.target.checked); saveWindow(e.target.checked); }} />
              <span><strong>Only share certain hours</strong>
                <em className="muted small">Early mornings and late nights stay yours.</em></span>
            </label>
            {windowOn && (
              <div className="inline wrap privacy-window" role="group" aria-label="Hours you share">
                <label className="db-field"><span>From</span>
                  <input type="time" value={winStart} aria-label="Share from"
                         onChange={e => setWinStart(e.target.value)}
                         onBlur={() => saveWindow(true)} /></label>
                <label className="db-field"><span>Until</span>
                  <input type="time" value={winEnd} aria-label="Share until"
                         onChange={e => setWinEnd(e.target.value)}
                         onBlur={() => saveWindow(true)} /></label>
              </div>
            )}
          </li>

          <li className="privacy-row">
            <label htmlFor="privacy-unfiled"><Icon name="tag" size={15} />Time with no category shows as</label>
            <select id="privacy-unfiled" value={settings.unfiled}
                    onChange={e => saveSettings.mutate({ unfiled: e.target.value as 'private' | 'busy' })}>
              <option value="private">Nothing</option>
              <option value="busy">Busy, to connections</option>
            </select>
          </li>

          <li>
            <label className="check-field">
              <input type="checkbox" checked={settings.findTime}
                     onChange={e => saveSettings.mutate({ findTime: e.target.checked })} />
              <span><strong>Let friends and meeting polls find times with me</strong>
                <em className="muted small">Uses only what you share — never titles, only when you are busy.</em></span>
            </label>
          </li>
        </ul>
      </Section>

      {/* ── what each category says ──────────────────────────── */}
      <Section id="visibility-title"
               icon="eye"
               title="What people can see"
               meta="per category, then per group and person"
               info={<>Sharing is expressed per category, which is what makes "I have not decided
          what this is" the safe state: a block filed under nothing is invisible to
          everybody unless you say otherwise above.</>}>

        {types.length === 0 && (
          <p className="muted small">
            No categories yet. They come from your hobbies and your activity types —
            until there are some, nothing can be shared even by accident.
          </p>
        )}

        <ul className="share-list">
          {types.map(t => (
            <li key={t.id}>
              <div className="share-what">
                <strong>{t.name}</strong>
                <span className="muted small">{LEVEL_COPY[t.visibility].says}</span>
              </div>
              <div className="inline wrap">
                <label className="db-field">
                  <span>Connections see</span>
                  <select value={t.visibility}
                          aria-label={`What connections see of ${t.name}`}
                          onChange={e => setVisibility.mutate({
                            t, body: { visibility: e.target.value as Level }
                          })}>
                    {(Object.keys(LEVEL_COPY) as Level[]).map(l => (
                      <option key={l} value={l}>{LEVEL_COPY[l].label}</option>
                    ))}
                  </select>
                </label>
                <label className={`chip chip-pick${t.isPublic ? ' is-on' : ''}`}>
                  <input type="checkbox" checked={t.isPublic}
                         aria-label={`Anybody with my code can see ${t.name}`}
                         onChange={e => setVisibility.mutate({
                           t, body: { isPublic: e.target.checked }
                         })} />
                  Anybody with my code
                </label>
              </div>
            </li>
          ))}
        </ul>
      </Section>

      {/* ── person by person ─────────────────────────────────── */}
      {accepted.length > 0 && (
        <Section id="person-title"
                 icon="user"
                 title="Person by person"
                 meta="beats their groups and the category"
                 info={<>A person's own setting wins over any group they are in and over the category —
            upwards or downwards. "Show them nothing" wins over everything, and they are not told.</>}>
          <ul className="person-privacy">
            {accepted.map(p => {
              const own = personShares.filter(s => s.viewerId === p.id).length;
              const isHidden = hidden.has(p.id);
              return (
                <li key={p.id}>
                  <details>
                    <summary>
                      <Icon name="user" size={15} />
                      <strong>{p.name}</strong>
                      {isHidden
                        ? <span className="chip chip-soft"><Icon name="lock" size={12} />sees nothing</span>
                        : own > 0 && <span className="chip chip-soft">{own} own setting{own === 1 ? '' : 's'}</span>}
                      <Icon name="chevronDown" size={14} />
                    </summary>

                    <label className="check-field">
                      <input type="checkbox" checked={isHidden}
                             onChange={e => hide.mutate({ p, hidden: e.target.checked })} />
                      <span><strong>Show {p.name} nothing</strong>
                        <em className="muted small">Nothing at all, whatever else says otherwise.</em></span>
                    </label>

                    {!isHidden && types.length > 0 && (
                      <div className="inline wrap">
                        {types.map(t => (
                          <label key={t.id} className="db-field">
                            <span>{t.name}</span>
                            <select value={ownSetting(p, t.id)}
                                    aria-label={`What ${p.name} sees of ${t.name}`}
                                    onChange={e => personShare.mutate({
                                      p, typeId: t.id,
                                      visibility: (e.target.value || null) as Level | null
                                    })}>
                              <option value="">Same as their groups</option>
                              {(Object.keys(LEVEL_COPY) as Level[]).map(l => (
                                <option key={l} value={l}>{LEVEL_COPY[l].label}</option>
                              ))}
                            </select>
                          </label>
                        ))}
                      </div>
                    )}

                    <button className="btn btn-ghost btn-sm" onClick={() => {
                      setPreviewAs(`person:${p.id}`);
                      document.getElementById('preview-title')?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
                    }}><Icon name="eye" size={15} />See it as {p.name}</button>
                  </details>
                </li>
              );
            })}
          </ul>
        </Section>
      )}

      {/* ── groups ───────────────────────────────────────────── */}
      <Section id="groups-title"
               icon="people"
               title="Groups"
               meta="one sentence instead of fourteen grants"
               info={<>"Work sees what I am doing; friends see that I am busy." A group's setting
          beats the category's, and a person's own setting beats the group's — which is
          how "everybody at work, except one person" gets said.</>}>

        <form className="inline wrap" onSubmit={(e: FormEvent) => {
          e.preventDefault();
          if (circleName.trim()) addCircle.mutate();
        }}>
          <input value={circleName} maxLength={40} placeholder="Work"
                 aria-label="Name of a new group"
                 onChange={e => setCircleName(e.target.value)} />
          <button className="btn btn-ghost btn-sm" type="submit" disabled={addCircle.isPending}>
            <Icon name="plus" size={16} />Add a group
          </button>
        </form>

        {circles.map(c => (
          <article key={c.id} className="circle">
            <div className="panel-head">
              <h3>{c.name}</h3>
              <div className="inline">
                <button className="btn btn-ghost btn-sm" onClick={() => setPreviewAs(`circle:${c.id}`)}>
                  <Icon name="eye" size={16} />See it as them
                </button>
                <button className="btn btn-ghost btn-sm btn-bad" onClick={() => dropCircle.mutate(c)}>
                  <Icon name="trash" size={16} />Remove
                </button>
              </div>
            </div>

            {c.members.length ? (
              <div className="inline wrap">
                {c.members.map(m => (
                  <span key={m.id} className="chip chip-soft member-chip">
                    {m.name}
                    <button type="button" className="chip-x" aria-label={`Take ${m.name} out of ${c.name}`}
                            onClick={() => dropMember.mutate({ circle: c, viewerId: m.id })}>
                      <Icon name="close" size={11} />
                    </button>
                  </span>
                ))}
              </div>
            ) : <p className="muted small">Nobody in it yet.</p>}

            {accepted.filter(p => !c.members.some(m => m.id === p.id)).length > 0 && (
              <div className="inline wrap">
                {accepted.filter(p => !c.members.some(m => m.id === p.id)).map(p => (
                  <button key={p.id} className="btn btn-ghost btn-sm"
                          onClick={() => addMember.mutate({ circle: c, viewerId: p.id })}>
                    <Icon name="plus" size={14} />{p.name}
                  </button>
                ))}
              </div>
            )}

            <div className="inline wrap">
              {types.map(t => {
                const level = c.shares.find(s => s.typeId === t.id)?.visibility ?? 'private';
                return (
                  <label key={t.id} className="db-field">
                    <span>{t.name}</span>
                    <select value={level}
                            aria-label={`What ${c.name} sees of ${t.name}`}
                            onChange={e => circleShare.mutate({
                              circle: c, typeId: t.id, visibility: e.target.value as Level
                            })}>
                      {(Object.keys(LEVEL_COPY) as Level[]).map(l => (
                        <option key={l} value={l}>{LEVEL_COPY[l].label}</option>
                      ))}
                    </select>
                  </label>
                );
              })}
            </div>
          </article>
        ))}
      </Section>

      {/* ── see it as them ───────────────────────────────────── */}
      <Section id="preview-title"
               icon="eye"
               title="See what they see"
               meta="your own day, through their eyes"
               info={<>Built by the same rules that decide what they really receive, so what is shown
          here is exactly what they would get — no more, no less.</>}>
        <div className="inline wrap">
          <label className="db-field">
            <span>As</span>
            <select value={previewAs} aria-label="See your day as" onChange={e => setPreviewAs(e.target.value)}>
              <option value="connection">Any connection</option>
              <option value="stranger">Somebody with your code</option>
              {circles.map(c => <option key={c.id} value={`circle:${c.id}`}>Somebody in {c.name}</option>)}
              {accepted.map(p => <option key={p.id} value={`person:${p.id}`}>{p.name}</option>)}
            </select>
          </label>
          <label className="db-field">
            <span>On</span>
            <input type="date" value={previewDate} aria-label="Which day"
                   onChange={e => setPreviewDate(e.target.value)} />
          </label>
        </div>

        {preview.isError && <p className="auth-err" role="alert">{(preview.error as Error).message}</p>}
        {preview.data && (
          <div className="preview-day" aria-live="polite">
            <p className="muted small">
              {preview.data.as} would see {friendlyDate(preview.data.day.date)} like this:
            </p>
            {preview.data.day.blocks.length === 0 ? (
              <p className="preview-empty">
                <Icon name="lock" size={15} />
                {WHY_EMPTY[preview.data.day.limited ?? ''] ?? 'Nothing visible on that day.'}
              </p>
            ) : (
              <ol className="preview-list">
                {preview.data.day.blocks.map((b, i) => (
                  <li key={i} className={b.title === 'Not free' ? 'is-busy' : ''}>
                    <time>{hhmm(b.startMin)}–{hhmm(Math.min(1439, b.endMin))}</time>
                    <span>{b.title}</span>
                    {b.typeName && <em className="muted small">{b.typeName}</em>}
                  </li>
                ))}
              </ol>
            )}
          </div>
        )}
      </Section>

      <Meetings connections={connections} />

      {rotating && (
        <ConfirmDialog
          title="Give yourself a new code?"
          description="The old one stops working immediately. People already connected stay
                       connected — this only closes the door to anybody still holding the old
                       code who has not used it."
          confirmLabel="New code"
          onClose={() => setRotating(false)}
          onConfirm={() => rotate.mutateAsync()} />
      )}
    </div>
  );
}
